import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Facility image carousel — auto-rotates, with prev/next controls and dot pagination
// Used on the About page to showcase ZT Health facilities.

const FACILITY_IMAGES = [
  {
    src: "https://customer-assets-v7afamib.emergentagent.net/job_corp-web-launch/artifacts/d6c5i27y_10068%20-%20ZTW%20Altus%20Stock%20Session%202026-HDR.webp",
    alt: "Altus Emergency Center exterior",
  },
  {
    src: "https://customer-assets-v7afamib.emergentagent.net/job_corp-web-launch/artifacts/ocm13we9_zt-health-page-img4.png",
    alt: "Neighbors Emergency Center main entrance",
  },
  {
    src: "https://customer-assets-v7afamib.emergentagent.net/job_corp-web-launch/artifacts/4cpxaq1r_4-_1051198.webp",
    alt: "Exceptional 24/7 Emergency Room at dusk",
  },
  {
    src: "https://customer-assets-v7afamib.emergentagent.net/job_corp-web-launch/artifacts/0wds29jj_Livingston.jpg",
    alt: "Exceptional Emergency Center — Livingston, TX",
  },
  {
    src: "https://customer-assets-v7afamib.emergentagent.net/job_corp-web-launch/artifacts/3kk8y6tn_10053%20-%20ZTW%20Altus%20Stock%20Session%202026-HDR.webp",
    alt: "Altus Emergency Centers facility exterior",
  },
  {
    src: "https://customer-assets-v7afamib.emergentagent.net/job_corp-web-launch/artifacts/yvpuud04_14-DJI_0303.webp",
    alt: "Altus Community Healthcare main entrance",
  },
];

export default function FacilityCarousel({ autoAdvanceMs = 5000 }) {
  const [idx, setIdx] = useState(0);
  const total = FACILITY_IMAGES.length;

  useEffect(() => {
    if (!autoAdvanceMs) return;
    const id = setInterval(() => {
      setIdx((v) => (v + 1) % total);
    }, autoAdvanceMs);
    return () => clearInterval(id);
  }, [autoAdvanceMs, total]);

  const prev = () => setIdx((v) => (v - 1 + total) % total);
  const next = () => setIdx((v) => (v + 1) % total);

  return (
    <div className="relative" data-testid="facility-carousel">
      <div className="relative w-full aspect-[16/10] overflow-hidden bg-slate-100">
        {FACILITY_IMAGES.map((img, i) => (
          <img
            key={img.src}
            src={img.src}
            alt={img.alt}
            data-testid={`facility-slide-${i}`}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
            style={{ opacity: i === idx ? 1 : 0 }}
            loading={i === 0 ? "eager" : "lazy"}
          />
        ))}

        {/* Subtle brand corner accent */}
        <div
          className="absolute bottom-0 left-0 h-1 transition-all duration-500"
          style={{ background: "var(--zt-teal)", width: `${((idx + 1) / total) * 100}%` }}
        />
      </div>

      {/* Controls row */}
      <div className="mt-5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2" data-testid="facility-dots">
          {FACILITY_IMAGES.map((img, i) => (
            <button
              key={img.src}
              type="button"
              data-testid={`facility-dot-${i}`}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => setIdx(i)}
              className="h-2 transition-all duration-300"
              style={{
                width: i === idx ? "32px" : "10px",
                background: i === idx ? "var(--zt-teal)" : "#CBD5E1",
              }}
            />
          ))}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="facility-prev"
            aria-label="Previous facility"
            onClick={prev}
            className="w-11 h-11 flex items-center justify-center border border-slate-300 bg-white hover:border-slate-900 transition-colors"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            data-testid="facility-next"
            aria-label="Next facility"
            onClick={next}
            className="w-11 h-11 flex items-center justify-center transition-colors hover:opacity-90 text-white"
            style={{ background: "var(--zt-teal)" }}
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
